import { useState, useEffect } from "react";
import api from "../../services/api";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import "./InventoryReport.css";

export default function InventoryReport() {
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filterStock, setFilterStock] = useState("all"); // all, bajo, agotado, normal

  // 🔹 Generar reporte (sin fechas, es snapshot actual)
  const handleGenerateReport = async () => {
    setLoading(true);
    try {
      const response = await api.get("/reports/inventory");

      setReportData(response.data);
      
      Swal.fire({
        icon: "success",
        title: "Reporte Generado",
        text: "El reporte de inventario se generó correctamente",
        timer: 2000,
        showConfirmButton: false
      });
    } catch (error) {
      console.error("Error generando reporte:", error);
      Swal.fire("Error", "No se pudo generar el reporte de inventario", "error");
    } finally {
      setLoading(false);
    }
  };

  // Cargar automáticamente al montar
  useEffect(() => {
    handleGenerateReport();
  }, []);

  // 🔹 Exportar a Excel
  const handleExportExcel = () => {
    if (!reportData) return;

    const workbook = XLSX.utils.book_new();

    // Hoja 1: Resumen
    const resumenData = [
      ["REPORTE DE INVENTARIO"],
      ["Fecha de Consulta:", reportData.fecha_consulta],
      [],
      ["RESUMEN"],
      ["Total Productos", reportData.resumen.total_productos],
      ["Productos Activos", reportData.resumen.productos_activos],
      ["Valor Inventario (Costo)", `$${reportData.resumen.valor_inventario_costo.toFixed(2)}`],
      ["Valor Inventario (Venta)", `$${reportData.resumen.valor_inventario_venta.toFixed(2)}`],
      ["Ganancia Potencial", `$${reportData.resumen.ganancia_potencial.toFixed(2)}`],
      ["Productos con Stock Bajo", reportData.resumen.productos_stock_bajo],
      ["Productos Agotados", reportData.resumen.productos_agotados],
    ];
    const wsResumen = XLSX.utils.aoa_to_sheet(resumenData);
    XLSX.utils.book_append_sheet(workbook, wsResumen, "Resumen");

    // Hoja 2: Detalle de Productos
    const detalleData = reportData.detalle.map(item => ({
      Código: item.codigo,
      Nombre: item.nombre,
      Stock: item.stock,
      "Stock Mínimo": item.stock_minimo,
      "Precio Compra": item.precio_compra,
      "Precio Venta": item.precio_venta,
      "Valor Costo": item.valor_costo,
      "Valor Venta": item.valor_venta,
      "Ganancia Potencial": item.ganancia_potencial,
      Estado: item.estado
    }));
    const wsDetalle = XLSX.utils.json_to_sheet(detalleData);
    XLSX.utils.book_append_sheet(workbook, wsDetalle, "Inventario");

    // Descargar
    const fileName = `Reporte_Inventario_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(workbook, fileName);

    Swal.fire({
      icon: "success",
      title: "Excel Descargado",
      timer: 2000,
      showConfirmButton: false
    });
  };

  // 🔹 Formatear moneda
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-SV', {
      style: 'currency',
      currency: 'USD'
    }).format(value);
  };

  // 🔹 Filtrar productos
  const getFilteredProducts = () => {
    if (!reportData) return [];
    
    switch (filterStock) {
      case "bajo":
        return reportData.detalle.filter(p => p.estado === "Bajo" || p.estado === "Crítico");
      case "agotado":
        return reportData.detalle.filter(p => p.estado === "Agotado");
      case "normal":
        return reportData.detalle.filter(p => p.estado === "Normal");
      default:
        return reportData.detalle;
    }
  };

  // Badge de estado
  const getEstadoBadge = (estado) => {
    const badges = {
      "Normal": "badge-success",
      "Bajo": "badge-warning",
      "Crítico": "badge-danger",
      "Agotado": "badge-dark"
    };
    return badges[estado] || "badge-secondary";
  };

  // Datos para gráfico (top 10 productos por valor)
  const getChartData = () => {
    if (!reportData) return [];
    return reportData.detalle
      .sort((a, b) => b.valor_venta - a.valor_venta)
      .slice(0, 10)
      .map(p => ({
        nombre: p.nombre.substring(0, 20),
        valor: p.valor_venta
      }));
  };

  return (
    <div className="inventory-report-container">
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">📦 Reporte de Inventario</h2>
          <div className="header-actions">
            <button 
              className="btn btn-primary" 
              onClick={handleGenerateReport}
              disabled={loading}
            >
              {loading ? "⏳ Actualizando..." : "🔄 Actualizar"}
            </button>
          </div>
        </div>

        <div className="card-body">
          {/* Loading */}
          {loading && (
            <div className="text-center mt-4">
              <div className="spinner"></div>
              <p className="mt-2">Generando reporte de inventario...</p>
            </div>
          )}

          {/* Resultados */}
          {reportData && !loading && (
            <div className="report-results">
              {/* Botón Exportar */}
              <div className="export-section">
                <button className="btn btn-success" onClick={handleExportExcel}>
                  📊 Exportar a Excel
                </button>
                <span className="text-muted ml-3">
                  Última actualización: {new Date(reportData.fecha_consulta).toLocaleString('es-ES')}
                </span>
              </div>

              {/* Resumen General */}
              <div className="summary-section">
                <h3 className="section-title">📊 Resumen de Inventario</h3>
                <div className="kpi-cards">
                  <div className="kpi-card kpi-primary">
                    <div className="kpi-icon">📦</div>
                    <div className="kpi-content">
                      <div className="kpi-label">Total Productos</div>
                      <div className="kpi-value">{reportData.resumen.total_productos}</div>
                    </div>
                  </div>

                  <div className="kpi-card kpi-info">
                    <div className="kpi-icon">💵</div>
                    <div className="kpi-content">
                      <div className="kpi-label">Valor Costo</div>
                      <div className="kpi-value">{formatCurrency(reportData.resumen.valor_inventario_costo)}</div>
                    </div>
                  </div>

                  <div className="kpi-card kpi-success">
                    <div className="kpi-icon">💰</div>
                    <div className="kpi-content">
                      <div className="kpi-label">Valor Venta</div>
                      <div className="kpi-value">{formatCurrency(reportData.resumen.valor_inventario_venta)}</div>
                    </div>
                  </div>

                  <div className="kpi-card kpi-warning">
                    <div className="kpi-icon">📈</div>
                    <div className="kpi-content">
                      <div className="kpi-label">Ganancia Potencial</div>
                      <div className="kpi-value">{formatCurrency(reportData.resumen.ganancia_potencial)}</div>
                    </div>
                  </div>
                </div>

                {/* Alertas de Stock */}
                <div className="alerts-section">
                  <div className="alert alert-warning">
                    ⚠️ <strong>{reportData.resumen.productos_stock_bajo}</strong> productos con stock bajo o crítico
                  </div>
                  {reportData.resumen.productos_agotados > 0 && (
                    <div className="alert alert-danger">
                      🚨 <strong>{reportData.resumen.productos_agotados}</strong> productos agotados
                    </div>
                  )}
                </div>
              </div>

              {/* Gráfico Top 10 */}
              <div className="chart-section">
                <h3 className="section-title">📈 Top 10 Productos por Valor de Inventario</h3>
                <div className="chart-container">
                  <ResponsiveContainer width="100%" height={400}>
                    <BarChart
                      data={getChartData()}
                      layout="vertical"
                      margin={{ top: 5, right: 30, left: 100, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" tickFormatter={(value) => `$${value.toFixed(0)}`} />
                      <YAxis dataKey="nombre" type="category" width={150} />
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                      <Legend />
                      <Bar dataKey="valor" fill="#8884d8" name="Valor de Venta" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Filtros */}
              <div className="filters-section">
                <h3 className="section-title">🔍 Filtrar por Estado</h3>
                <div className="filter-buttons">
                  <button 
                    className={`btn ${filterStock === 'all' ? 'btn-primary' : 'btn-outline-primary'}`}
                    onClick={() => setFilterStock('all')}
                  >
                    Todos ({reportData.detalle.length})
                  </button>
                  <button 
                    className={`btn ${filterStock === 'normal' ? 'btn-success' : 'btn-outline-success'}`}
                    onClick={() => setFilterStock('normal')}
                  >
                    Normal ({reportData.detalle.filter(p => p.estado === 'Normal').length})
                  </button>
                  <button 
                    className={`btn ${filterStock === 'bajo' ? 'btn-warning' : 'btn-outline-warning'}`}
                    onClick={() => setFilterStock('bajo')}
                  >
                    Bajo/Crítico ({reportData.resumen.productos_stock_bajo})
                  </button>
                  <button 
                    className={`btn ${filterStock === 'agotado' ? 'btn-danger' : 'btn-outline-danger'}`}
                    onClick={() => setFilterStock('agotado')}
                  >
                    Agotados ({reportData.resumen.productos_agotados})
                  </button>
                </div>
              </div>

              {/* Tabla de Inventario */}
              <div className="detail-section">
                <h3 className="section-title">📋 Detalle de Productos</h3>
                <div className="table-container">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Código</th>
                        <th>Nombre</th>
                        <th>Stock</th>
                        <th>Stock Mín.</th>
                        <th>P. Compra</th>
                        <th>P. Venta</th>
                        <th>Valor Costo</th>
                        <th>Valor Venta</th>
                        <th>Ganancia Pot.</th>
                        <th>Estado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getFilteredProducts().map((item, index) => (
                        <tr key={index}>
                          <td>{item.codigo}</td>
                          <td>{item.nombre}</td>
                          <td className="text-center">
                            <strong className={item.stock === 0 ? 'text-danger' : item.stock < item.stock_minimo ? 'text-warning' : ''}>
                              {item.stock}
                            </strong>
                          </td>
                          <td className="text-center">{item.stock_minimo}</td>
                          <td className="text-right">{formatCurrency(item.precio_compra)}</td>
                          <td className="text-right">{formatCurrency(item.precio_venta)}</td>
                          <td className="text-right">{formatCurrency(item.valor_costo)}</td>
                          <td className="text-right">{formatCurrency(item.valor_venta)}</td>
                          <td className="text-right text-success">
                            {formatCurrency(item.ganancia_potencial)}
                          </td>
                          <td>
                            <span className={`badge ${getEstadoBadge(item.estado)}`}>
                              {item.estado}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Sin datos */}
          {!reportData && !loading && (
            <div className="no-data">
              <p>📭 Cargando inventario...</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}